import { makeAutoObservable } from 'mobx';

const DEFAULT_IDLE_TIMEOUT = 5 * 60 * 1000;

const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const;

export class UserPresenceStore {
  isVisible = document.visibilityState === 'visible';

  isIdle = false;

  private lastActivityAt = Date.now();

  private idleCheckTimeout?: SetTimeout;

  constructor(private readonly idleTimeout: number = DEFAULT_IDLE_TIMEOUT) {
    makeAutoObservable<UserPresenceStore, 'lastActivityAt' | 'idleCheckTimeout' | 'idleTimeout'>(this, {
      lastActivityAt: false,
      idleCheckTimeout: false,
      idleTimeout: false,
    });

    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('focus', this.handleActivity);
    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, this.handleActivity, { capture: true, passive: true });
    });

    this.scheduleIdleCheck(this.idleTimeout);
  }

  get isActive() {
    return this.isVisible && !this.isIdle;
  }

  dispose() {
    clearTimeout(this.idleCheckTimeout);

    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('focus', this.handleActivity);
    ACTIVITY_EVENTS.forEach((eventName) => {
      window.removeEventListener(eventName, this.handleActivity, { capture: true });
    });
  }

  private handleVisibilityChange = () => {
    this.isVisible = document.visibilityState === 'visible';

    if (this.isVisible) {
      this.handleActivity();
    }
  };

  private handleActivity = () => {
    this.lastActivityAt = Date.now();

    if (this.isIdle) {
      this.isIdle = false;
      this.scheduleIdleCheck(this.idleTimeout);
    }
  };

  private checkIdle = () => {
    const idleFor = Date.now() - this.lastActivityAt;

    if (idleFor >= this.idleTimeout) {
      this.isIdle = true;
    } else {
      this.scheduleIdleCheck(this.idleTimeout - idleFor);
    }
  };

  private scheduleIdleCheck(delay: number) {
    clearTimeout(this.idleCheckTimeout);
    this.idleCheckTimeout = setTimeout(this.checkIdle, delay);
  }
}
